import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useContextSelector } from "use-context-selector";
import { StateContext } from "../../contexts/StateContext";
import { createChitOnboard } from "../../api/AdminApi";

const AdminChitCreator = () => {
  const role = useContextSelector(StateContext, (state) => state.role);
  const navigate = useNavigate();

  const [chitDetails, setChitDetails] = useState({
    chitTitle: "",
    chitValue: "",
    startDate: "",
    endDate: "",
    months: "",
    members: "",
    fullChitBidderAmount: "",
    startFullChitNonBidder: "",
    monthlyDecrease: "",
  });
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [scheduleData, setScheduleData] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-calculate end date when startDate or months change
  useEffect(() => {
    if (chitDetails.startDate && chitDetails.months) {
      const startDate = new Date(chitDetails.startDate);
      const months = parseInt(chitDetails.months);
      if (!isNaN(months) && months > 0) {
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + months);
        const formattedEndDate = endDate.toISOString().split("T")[0];
        setChitDetails((prev) => ({
          ...prev,
          endDate: formattedEndDate,
        }));
      }
    }
  }, [chitDetails.startDate, chitDetails.months]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setChitDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const showNotification = (message, type) => {
    if (type === "success") toast.success(message);
    if (type === "error") toast.error(message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (scheduleData.length === 0) {
      showNotification("Please generate the scheme first", "error");
      setIsSubmitting(false);
      return;
    }

    const chitPayload = {
      ...chitDetails,
      scheduleData: scheduleData.filter((row) => row.month !== 0),
    };

    try {
      const response = await createChitOnboard(chitPayload);
      showNotification(
        response.data.message || "Chit created successfully!",
        "success"
      );
      setTimeout(() => {
        setChitDetails({
          chitTitle: "",
          chitValue: "",
          startDate: "",
          endDate: "",
          months: "",
          members: "",
          fullChitBidderAmount: "",
          startFullChitNonBidder: "",
          monthlyDecrease: "",
        });
        setScheduleData([]);
        setShowSaveButton(false);
        setIsSubmitting(false);
        navigate("/chit");
      }, 1500);
    } catch (error) {
      showNotification("Error creating chit onboard: " + error.message, "error");
      setIsSubmitting(false);
    }
  };

  // ✅ Helper: Recalculate total row
  const recalculateTotals = (data) => {
    const totalData = data.filter((row) => row.month !== 0);

    const totalFullNonChit = totalData.reduce(
      (sum, row) => sum + Number(row.fullNonChit),
      0
    );
    const totalFullChit = totalData.reduce(
      (sum, row) => sum + Number(row.fullChit),
      0
    );
    const totalAmount = totalData.reduce(
      (sum, row) => sum + Number(row.totalAmount),
      0
    );

    const newTotalRow = {
      month: 0,
      fullNonChit: Math.round(totalFullNonChit),
      fullChit: Math.round(totalFullChit),
      totalAmount: Math.round(totalAmount),
    };

    return [...totalData, newTotalRow];
  };

  // ✅ Corrected Chit Generation Logic
// ✅ Corrected Chit Generation Logic
const generateSchedule = () => {
  const {
    months,
    members,
    startFullChitNonBidder,
    fullChitBidderAmount,
    monthlyDecrease,
  } = chitDetails;

  const numMonths = parseInt(months, 10);
  const numMembers = parseInt(members, 10);
  const startNonBidder = Number(startFullChitNonBidder);
  const bidderAmount = Number(fullChitBidderAmount);
  const decreaseStep = parseInt(monthlyDecrease || 0, 10);

  if (!numMonths || !startFullChitNonBidder || !fullChitBidderAmount || !numMembers) {
    showNotification("Please fill in all chit details correctly.", "error");
    return;
  }

  const calculatedData = [];
  let currentFullNonChit = startNonBidder;

  for (let i = 1; i <= numMonths; i++) {
    let totalAmount = 0;
    let formula = "—";

    if (i === 1) {
      // First month: only non-bidders pay
      totalAmount = 0;
    } else {
      // From second month onwards
      const previousNonBiddersSum = (startNonBidder + (startNonBidder - decreaseStep * (i - 2))) * (i - 1) / 2;
      const remainingBidders = numMembers - (i - 1);
      
      totalAmount = Math.round(previousNonBiddersSum + (bidderAmount * remainingBidders));
      formula = `(${Array.from({length: i-1}, (_, idx) => startNonBidder - decreaseStep * idx).join("+")}) + (${bidderAmount}×${remainingBidders})`;
    }

    calculatedData.push({
      month: i,
      fullNonChit: Math.round(currentFullNonChit),
      fullChit: i === 1 ? 0 : bidderAmount, // First month has no bidders
      totalAmount: totalAmount,
      formula: formula,
    });

    currentFullNonChit -= decreaseStep;
  }

  const finalSchedule = recalculateTotals(calculatedData);
  setScheduleData(finalSchedule);
  setShowSaveButton(true);
  showNotification("Scheme generated successfully!", "success");
};

  // Add total summary row




  const handleTableChange = (e, month, field) => {
    const value = e.target.value;
    const newData = scheduleData.map((row) => {
      if (row.month === month) {
        const updatedRow = {
          ...row,
          [field]: Number(value),
        };
        if (field === "fullNonChit" || field === "fullChit") {
          updatedRow.totalAmount = Math.round(
            (field === "fullNonChit" ? Number(value) : updatedRow.fullNonChit) +
              (field === "fullChit" ? Number(value) : updatedRow.fullChit)
          );
        }
        return updatedRow;
      }
      return row;
    });

    const finalData = recalculateTotals(newData);
    setScheduleData(finalData);
  };

  const handleDoubleClick = (month, field) => {
    setEditingCell({ month, field });
  };

  const handleBlur = () => {
    setEditingCell(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Chit Onboarding
              </h1>
              <p className="text-gray-600 mt-2">
                Create and manage chit schemes for your organization
              </p>
            </div>
            <Link
              to={"/"}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white font-semibold rounded-xl shadow-lg hover:from-gray-700 hover:to-gray-800 transition-all duration-200 transform hover:-translate-y-0.5"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
              Back to Dashboard
            </Link>
          </div>
        </div>

        {/* Form Section */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
            <h2 className="text-xl font-bold text-white">Create New Chit Scheme</h2>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {[
                { id: "chitTitle", label: "Chit Title", type: "text", placeholder: "Enter chit title" },
                { id: "chitValue", label: "Chit Value", type: "number", placeholder: "Enter chit value" },
                { id: "startDate", label: "Start Date", type: "date" },
                { id: "endDate", label: "End Date", type: "date", readOnly: true, className: "bg-gray-100 cursor-not-allowed" },
                { id: "months", label: "Months", type: "number", placeholder: "Number of months" },
                { id: "members", label: "Members", type: "number", placeholder: "Number of members" },
                { id: "fullChitBidderAmount", label: "Chit Settled Member to Pay", type: "number", placeholder: "Full chit bidder amount" },
                { id: "startFullChitNonBidder", label: "Chit Non-Settled Member to Pay", type: "number", placeholder: "Starting non-bidder amount" },
                { id: "monthlyDecrease", label: "Monthly Decrease", type: "number", placeholder: "e.g., 50" },
              ].map((field) => (
                <div key={field.id} className="space-y-2">
                  <label htmlFor={field.id} className="block text-sm font-semibold text-gray-700">
                    {field.label}
                  </label>
                  <input
                    type={field.type}
                    id={field.id}
                    name={field.id}
                    value={chitDetails[field.id]}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    readOnly={field.readOnly}
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 shadow-sm ${field.className || ''}`}
                    required={!field.readOnly}
                  />
                  {field.id === "endDate" && (
                    <p className="text-xs text-gray-500 mt-1">
                      End date is calculated automatically from start date and months
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-200">
              {showSaveButton && (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`inline-flex items-center justify-center px-8 py-3 font-semibold rounded-lg shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 ${
                    isSubmitting
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Save Chit Onboard
                    </>
                  )}
                </button>
              )}

              <button
                type="button"
                onClick={generateSchedule}
                className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:-translate-y-0.5"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                Generate Scheme
              </button>
            </div>
          </form>
        </div>

        {/* Table */}
        {scheduleData.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4">
              <h2 className="text-xl font-bold text-white">Payment Schedule</h2>
            </div>

            <div className="p-6">
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {["Month", "Non-Settled Member", "Settled Member", "Total Amount"].map(
                        (header) => (
                          <th
                            key={header}
                            className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                          >
                            {header}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {scheduleData.map((row, index) => (
                      <tr
                        key={index}
                        className={`transition-colors duration-150 ${
                          row.month === 0
                            ? "bg-gradient-to-r from-gray-50 to-gray-100 font-bold"
                            : "hover:bg-blue-50"
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {row.month === 0 ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-200 text-gray-800">
                              Total
                            </span>
                          ) : (
                            row.month
                          )}
                        </td>

                        {["fullNonChit", "fullChit", "totalAmount"].map((field) => (
                          <td
                            key={field}
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-700"
                          >
                            {row.month !== 0 &&
                            editingCell?.month === row.month &&
                            editingCell?.field === field ? (
                              <input
                                type="number"
                                value={row[field]}
                                onChange={(e) =>
                                  handleTableChange(e, row.month, field)
                                }
                                onBlur={handleBlur}
                                autoFocus
                                className="w-full px-3 py-1 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              />
                            ) : (
                              <span
                                onDoubleClick={() =>
                                  row.month !== 0 &&
                                  handleDoubleClick(row.month, field)
                                }
                                className={`block w-full cursor-pointer transition-colors duration-150 ${
                                  row.month === 0
                                    ? "font-bold text-gray-900"
                                    : "hover:text-blue-600"
                                }`}
                              >
                                {row[field]}
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex items-center text-sm text-gray-500 bg-blue-50 px-4 py-3 rounded-lg border border-blue-100">
                <svg
                  className="w-5 h-5 mr-2 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M12 20.5a8.5 8.5 0 110-17 8.5 8.5 0 010 17z"
                  />
                </svg>
                Double-click a cell to edit it manually.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminChitCreator;
